import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";

import { ExportReportParamsObjectTypeEnum } from "api";
import PrimaryButton from "components/PrimaryButton";
import { useAuth } from "contexts/AuthProvider";
import { FilterStateValue } from "contexts/RequestParamsProvider";
import useSendCsvEmail from "hooks/api/documents/useSendCsvEmail";
import ValidCsvEmailRecipentSelect from "./ValidCsvEmailRecipentSelect";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";

const MODAL_SIZE = mobileStyleBreakpoint("sm", "lg");

export const MAX_MESSAGE_LENGTH = 150;

const EmailCsvModal = ({
  isOpen,
  onClose,
  objectType,
  searchString,
  filters,
}: {
  objectType: ExportReportParamsObjectTypeEnum;
  searchString: string;
  filters: Record<string, FilterStateValue>;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const cardCodes = useSelectedCustomerCode();
  const { username } = useAuth();
  const [message, setMessage] = useState("");
  const [sendCopyToSelf, setSendCopyToSelf] = useState(true);
  const toast = useToast();
  const { mutate: sendCsvEmail, isLoading } = useSendCsvEmail(cardCodes);
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);

  const recipientEmails = useMemo(
    () => [
      ...selectedUsers.map((option) => option.value),
      ...(sendCopyToSelf ? [username] : []),
    ],
    [selectedUsers, sendCopyToSelf, username]
  );

  const onEmailCsv = useCallback(() => {
    sendCsvEmail(
      {
        recipientEmails,
        message,
        cardCodes,
        objectType,
        searchString: searchString || undefined,
        ...filters,
      },
      {
        onSuccess: () => {
          toast({
            position: "top-right",
            description: "Email(s) successfully sent",
            duration: 2000,
            status: "success",
          });
        },
        onError: (error) => {
          console.error("error sending email: ", error);
          toast({
            position: "top-right",
            description: "Error sending email",
            duration: 2000,
            status: "error",
          });
        },
      }
    );
  }, [
    cardCodes,
    filters,
    message,
    objectType,
    recipientEmails,
    searchString,
    sendCsvEmail,
    toast,
  ]);

  const charactersLeft = MAX_MESSAGE_LENGTH - message.length;

  const handleInputChange = (e: { target: { value: string } }) => {
    const newInputValue = e.target.value;

    if (charactersLeft === 0 && newInputValue.length > message.length) {
      return;
    }
    setMessage(newInputValue);
  };

  const closeModal = () => {
    setSendCopyToSelf(true);
    setSelectedUsers([]);
    setMessage("");
    onClose();
  };

  const handleClick = () => {
    onEmailCsv();
    closeModal();
  };

  const isLargerThanMobile = useBreakpointValue({ base: false, sm: true });

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={closeModal}
      isCentered={isLargerThanMobile}
      size={MODAL_SIZE}
    >
      <ModalOverlay />
      <ModalContent w="496px" mt={{ base: 0, sm: 64 }}>
        <form>
          <ModalHeader fontSize="24px" fontWeight="black">
            Email .CSV
          </ModalHeader>
          <ModalBody>
            <Text textStyle="body2" mb={4}>
              Only users who have access to this data in the customer portal
              will be listed as available recipients.
            </Text>
            <FormControl>
              <FormLabel>Send to</FormLabel>
              <Box mb={4}>
                <ValidCsvEmailRecipentSelect
                  objectType={objectType}
                  searchString={searchString}
                  filters={filters}
                  selectedUsers={selectedUsers}
                  setSelectedUsers={setSelectedUsers}
                />
              </Box>
            </FormControl>
            <FormControl>
              <FormLabel>Message (optional)</FormLabel>
              <Textarea value={message} mb={1} onChange={handleInputChange} />
            </FormControl>

            <Text
              color={charactersLeft >= 0 ? "grey.400" : "warningRed"}
              textStyle="label2"
              mb={4}
            >
              {charactersLeft} characters left
            </Text>
            <Checkbox
              isChecked={sendCopyToSelf}
              onChange={(e) => setSendCopyToSelf(e.target.checked)}
            >
              <Text color="grey.700" fontSize="14px">
                Send me a copy
              </Text>
            </Checkbox>
          </ModalBody>
          <ModalFooter mt={1}>
            <PrimaryButton
              color="navy.600"
              mr={3}
              onClick={closeModal}
              variant="outline"
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              isDisabled={
                recipientEmails.length === 0 || charactersLeft < 0 || isLoading
              }
              onClick={handleClick}
              variant="solid"
            >
              Send
            </PrimaryButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default EmailCsvModal;
